// Must be first: configures the `FfV5` class-name prefix before any
// `@mui/material` component module can bake in the default `Mui` one.
import "./classNamePrefix";

import { createTheme } from "@mui/material/styles";
import type { CSSProperties } from "@mui/material/styles";
import {
  breakpointValues,
  color,
  componentMetrics as m,
  mq,
  paletteTokens,
  spacingUnit,
  typographyTokens,
} from "./tokens";

/**
 * MUI **v9** theme — the v5+ side of the PM5 coexistence bridge
 * (docs/epics/pm5-mui-v5-migration.md, T0).
 *
 * Fed from exactly the same `tokens.ts` as the legacy v4 theme
 * (`src/components/Theme.js`), so a brand value can never be changed on one
 * side only (risk 4). The v4 `overrides` blocks are ported below to
 * `components.<Slot>.styleOverrides`; every place where the slot could not be
 * carried across 1:1 is called out in a comment rather than guessed at.
 */

declare module "@mui/material/styles" {
  // The app reads these non-standard palette keys all over the codebase; they
  // came from the v4 theme and are preserved verbatim.
  interface CommonColors {
    gray: string;
    darkgray: string;
    lightgray: string;
    dark: string;
    upgrade: string;
  }
  interface TypeBackground {
    ffGreen: string;
    ffNavy: string;
    ffNavyLighter: string;
    ffNavyLight: string;
    agLife: string;
    navy: string;
    dark: string;
    upgrade: string;
    upgradeHover: string;
    upgradeAlt: string;
    upgradeBG: string;
  }
  // Two non-standard typography variants inherited from the v4 theme.
  interface TypographyVariants {
    bodysml: CSSProperties;
    a: CSSProperties;
  }
  interface TypographyVariantsOptions {
    bodysml?: CSSProperties;
    a?: CSSProperties;
  }
}

const themeV5 = createTheme({
  // v5+ changed the default breakpoint values (sm 600 / md 900 / lg 1200 /
  // xl 1536). Every hand-written media query in this codebase — and the
  // `mq.*` strings the theme itself emits — assumes the v4 grid, so pin it.
  // Unpinning is a visual change, not a migration step.
  breakpoints: { values: { ...breakpointValues } },

  // The v4 theme never set `palette.type`, so there is nothing to rename to
  // `palette.mode`; both themes are therefore in the default light mode.
  palette: paletteTokens,
  spacing: spacingUnit,
  typography: typographyTokens,

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          // PARITY RESTORE (T0 pilot — Footer's "More info" button, the first
          // converted Button). `typography.button` carries a non-typographic
          // `backgroundColor: #99ca3c`, and v4's Button root did nothing but
          // `...theme.typography.button`, so every v4 button is lime-backed by
          // default. v5+ still spreads `theme.typography.button` but then
          // re-declares `backgroundColor: var(--variant-textBg)` for the text
          // variant, which overwrites the same object key — so the lime is
          // silently lost. Re-declaring it here (styleOverrides run after the
          // base/variant styles) restores the v4 default; the `contained` /
          // `outlined` slots below still win for their own variants, exactly as
          // in v4.
          backgroundColor: color.ffGreen,
          border: `${m.button.borderWidth} solid ${color.ffGreen}`,
          borderRadius: m.button.borderRadius,
          margin: m.button.margin,
          // Inert in v4 too (empty rule bodies); kept so the two override sets
          // stay diff-able line for line.
          [mq.sm]: {},
          [mq.md]: {},
          "&:hover": {
            backgroundColor: color.ffGreenHover,
            border: `${m.button.borderWidth} solid ${color.ffGreenHover}`,
            boxShadow: "none",
            transition: m.button.transition,
            "@media (hover: none)": {
              backgroundColor: color.ffGreenHover,
            },
          },
        },
        // MAPPING NOTE — v4 `containedSecondary` has no v5+ equivalent slot:
        // the variant/colour class pair was split into `contained` +
        // `colorSecondary`, and `Button`'s overridesResolver no longer looks up
        // `${variant}${Color}` keys at all. Re-expressed as an ownerState-gated
        // branch of `contained`, which is exactly the v4 condition
        // (variant="contained" AND color="secondary"). Deliberately NOT written
        // as a `&.FfV5Button-colorSecondary` selector — that would couple the
        // theme to the class-name prefix.
        contained: ({ ownerState }) => ({
          ...(ownerState.color === "secondary" && {
            backgroundColor: color.buttonSecondary,
            color: color.white,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: color.buttonSecondaryHover,
              border: "none",
              boxShadow: "none",
              transition: m.button.transition,
            },
          }),
        }),
        text: {
          padding: m.button.textPadding,
          lineHeight: m.button.lineHeight,
        },
        outlined: {
          lineHeight: m.button.lineHeight,
          padding: m.button.outlinedPadding,
          backgroundColor: "transparent",
          border: `${m.button.borderWidth} solid ${color.textPrimary}`,
          "&:hover": {
            backgroundColor: color.blackWash,
            border: `${m.button.borderWidth} solid ${color.textPrimary}`,
            color: color.textPrimary,
            boxShadow: "none",
            transition: m.button.transition,
          },
        },
        sizeSmall: {
          padding: m.button.smallPadding,
        },
        sizeLarge: {
          padding: m.button.largePadding,
          fontSize: m.button.largeFontSize,
          margin: m.button.largeMargin,
          [mq.sm]: {
            fontSize: m.button.largeFontSizeUp,
            lineHeight: m.button.largeLineHeightSm,
          },
          [mq.md]: {
            fontSize: m.button.largeFontSizeUp,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          // zIndex: "9999999 !important"
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        // PARITY RESOLVED (T0 pilot — Footer, the first converted Divider).
        // The slot maps 1:1, but the *base* component differs: v4's Divider
        // root was `{ height: 1, border: none, backgroundColor: palette.divider }`
        // — this override then repainted it as a 5px navy bar. v5+ dropped the
        // background approach and draws the rule with `borderBottomWidth: thin`
        // over `borderColor: palette.divider`, so the same override yields the
        // 5px bar PLUS a hairline underneath it. `borderBottomWidth: 0` is the
        // v5+ spelling of v4's `border: none`, restoring the v4 rendering for
        // every Divider on the v5 side — not a visual decision, a port of one.
        root: {
          height: m.divider.height,
          backgroundColor: color.ffNavyLight,
          borderBottomWidth: 0,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: color.ffNavy,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: color.darkSurface,
          color: color.darkSurfaceText,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: color.ffNavy,
          color: color.white,
        },
      },
    },
    // NOT PORTED — v4 had `MuiList: { padding: "0" }`, which is malformed:
    // `padding` is a class-key slot, so its value must be a style object, not
    // a string. JSS ignores it, so the rule has never had any effect. Carrying
    // it over would be inventing a style that production has never shown.
    // Left out on purpose; the v4 block is kept verbatim in Theme.js.
    MuiListItemButton: {
      styleOverrides: {
        // MAPPING NOTE — v4 `MuiListItem: { button: {...} }` styled a ListItem
        // rendered with the `button` prop. v5+ removed that prop entirely and
        // split the behaviour out into a separate `ListItemButton` component,
        // so the slot has no v4-shaped home. Relocated to `MuiListItemButton`'s
        // root, which is the component v4's `<ListItem button>` now converts to.
        root: {
          "&:hover": {
            backgroundColor: color.darkSurface,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontWeight: m.tableCell.fontWeight,
          padding: m.tableCell.padding,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& label": {
            color: color.ffNavy,
            fontWeight: m.formControlLabel.fontWeight,
            fontSize: m.formControlLabel.fontSize,
            width: m.formControlLabel.width,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: color.ffGreen,
          "&:focus": {
            outline: "none",
          },
          "&:hover": {
            outline: "none",
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: m.stepper.padding,
          [mq.sm]: {
            padding: m.stepper.paddingUp,
          },
          [mq.md]: {
            padding: m.stepper.paddingUp,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: color.stepIconIdle,
          fontSize: m.stepIcon.fontSize,
          [mq.sm]: {
            fontSize: m.stepIcon.fontSize,
          },
          [mq.md]: {
            fontSize: m.stepIcon.fontSize,
          },
          // MAPPING NOTE — v4 had a sibling `active: {...}` block. In v5+
          // `StepIcon`'s Root has no overridesResolver, so only the `root` and
          // `text` styleOverrides keys are ever applied — an `active` key would
          // be silently dropped. Folded into `root` as a nested selector.
          // `Mui-active` is a *global state* class: `generateUtilityClass`
          // hard-codes the `Mui` prefix for state classes, so the `FfV5` rename
          // does not apply here and this selector is stable.
          "&.Mui-active": {
            color: `${color.ffGreen} !important`,
          },
        },
        text: {
          fill: color.white,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: m.tooltip.popperZIndex,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: color.ffNavy,
          fontSize: m.stepLabel.fontSize,
          lineHeight: m.stepLabel.lineHeight,
          [mq.sm]: {
            fontSize: m.stepLabel.fontSizeUp,
            lineHeight: m.stepLabel.lineHeight,
          },
          [mq.md]: {
            fontSize: m.stepLabel.fontSizeUp,
            lineHeight: m.stepLabel.lineHeight,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: m.formLabel.lineHeight,
          color: color.ffNavy,
          fontWeight: m.formLabel.fontWeight,
          fontSize: m.formLabel.fontSize,
        },
      },
    },
    // NOT a v4 override block — v4's theme had no `MuiInputLabel` entry. This
    // is the same shape as the `MuiInputBase` height restore below and the
    // `MuiDivider` hairline above: a *base component* metric that moved between
    // the majors, restated here because `themeV5` is the only shared home for
    // "what v4 actually rendered".
    //
    // PARITY RESTORE (T2 visual gate, found while fixing the SignIn label
    // colour — the shift was invisible while the labels were navy-on-navy).
    // v4's InputLabel positioned the standard-variant label with
    // `formControl: translate(0, 24px)`, `marginDense: translate(0, 21px)` and
    // `shrink: translate(0, 1.5px) scale(0.75)`. v9 moved all three up —
    // `translate(0, 20px)`, `size="small"` `translate(0, 17px)`, and
    // `shrink: translate(0, -1.5px)`. Measured in Chromium on both themes'
    // emitted CSS, label top relative to its FormControl: shrunk **1.5px →
    // -1.5px**, resting **24px → 20px**. The FormControl's own height is
    // unchanged (the label is absolutely positioned), so this is purely the
    // label sliding up 3–4px over the field.
    //
    // Scoped to the standard variant: v4's `filled` / `outlined` classes
    // overrode these transforms with their own, and every converted control
    // pins `variant="standard"` (guide §2, "TextField default variant"), so
    // restating the other two variants would be inventing rules this codebase
    // has never rendered.
    //
    // The second half is the single-line clamp v9 added to the same root —
    // `whiteSpace: nowrap` + `overflow: hidden` + `textOverflow: ellipsis` +
    // `maxWidth: 100%` (133% when shrunk). v4's root had none of it. That is
    // not a wrapping nicety here: this theme's `MuiFormLabel` pins
    // `lineHeight: 0.5`, so a 19.2px label lives in a 9.6px box and
    // `overflow: hidden` shaves the ascenders off. Measured on the built login
    // page: label `clientHeight: 10` vs `scrollHeight: 16`, and "Email" renders
    // with the top of the E/i/l cut away.
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant && ownerState.variant !== "standard") {
            return {};
          }
          // v4's InputLabel root was only `{ display: block, transformOrigin }`
          // — the four below are v9 additions being reset back to their initial
          // values, which is what v4 rendered.
          const v4Root = {
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            maxWidth: "none",
          } as const;
          // Written as an if-chain rather than three merged objects so the v4
          // sheet order is explicit: `shrink` was declared after `marginDense`,
          // which was declared after `formControl`, and all three are the same
          // single-class specificity — so the later key won.
          if (ownerState.shrink) {
            return { ...v4Root, transform: "translate(0, 1.5px) scale(0.75)" };
          }
          if (ownerState.size === "small") {
            return { ...v4Root, transform: "translate(0, 21px) scale(1)" };
          }
          if (ownerState.formControl) {
            return { ...v4Root, transform: "translate(0, 24px) scale(1)" };
          }
          return v4Root;
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          borderBottom: "none",
          "&:before": {
            borderBottom: "none",
            // Inert in v4 as well — an empty `content` is not valid CSS, so
            // the pseudo-element never rendered. Carried over unchanged rather
            // than "fixed", which would be a visual change.
            content: "",
            "&:hover": {
              borderBottom: "none",
              outline: "none",
            },
          },
          "&:after": {
            borderBottom: "none",
            "&:hover": {
              borderBottom: "none",
              outline: "none",
            },
          },
          "&&&&:hover:before": {
            borderBottom: "none",
            outline: "none",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // MAPPING NOTE — renamed slot. In v4, `MuiSelect-root` and
        // `MuiSelect-select` were both applied to the same element (the value
        // display), with a `// TODO v5: merge root and select` in v4's own
        // SelectInput source. v5+ carried out that merge: `select` is the
        // display element and `root` is now the wrapping Input. `marginTop: 0`
        // was written for the display element, so it moves to `select`.
        select: {
          marginTop: m.select.marginTop,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // PARITY RESTORE (T2 visual gate — every converted text field). Same
        // shape as the `MuiDivider.borderBottomWidth` fix above: the slot maps
        // 1:1 but the *base* component's own metric moved. v4's `InputBase`
        // input declared `height: '1.1876em'`; v9's declares
        // `height: '1.4375em'` — and this override never named `height`, so the
        // library default shows through. The v4 theme's padding then sits round
        // a box that is 0.2499em taller. Measured in Chromium on the emitted
        // CSS of both themes: login email/password 45.09px → 48.69px, the
        // giftcard code field (20px inline font) 51.75px → 56.75px. Restating
        // v4's own value is a port of the v4 rendering, not a design choice.
        // The exception is v4's, too: v4's `inputMultiline` rule was declared
        // after `input` in the same JSS sheet, so `height: auto` won for a
        // textarea. v9's multiline variant says the same thing, but a flat
        // `height` here would land after it and pin every textarea to one line.
        // (No converted file uses `multiline` yet — MemberSurvey is T5.)
        input: ({ ownerState }) => ({
          marginTop: m.input.marginTop,
          fontSize: m.input.fontSize,
          lineHeight: m.input.lineHeight,
          // background:"#eee",
          padding: m.input.padding,
          // borderBottom: "none",
          border: `${m.input.borderWidth} solid ${color.inputBorder}`,
          borderRadius: m.input.borderRadius,
          ...(!ownerState.multiline && { height: "1.1876em" }),
          "&:hover": {
            // borderBottom: "none"
          },
          "&:focus": {
            // borderBottom: "none"
            border: `${m.input.borderWidth} solid ${color.ffNavy}`,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: m.outlinedInput.padding,
          fontSize: m.outlinedInput.fontSize,
        },
        root: {
          // `Mui-error` is a global state class and keeps the `Mui` prefix
          // under the `FfV5` generator — see classNamePrefix.ts.
          "&.Mui-error": {
            borderColor: `${color.errorMain} !important`,
          },
        },
      },
    },
    MuiAlert: {
      // DONE in T2/signin — the release smoke used to locate
      // `.MuiAlert-filledError` (e2e/release-smoke.spec.ts). Now that SignIn is
      // converted it is `.FfV5Alert-filled.FfV5Alert-colorError`: the v5+ line
      // dropped the combined `filledError` slot, so variant and severity are
      // two separate classes (`alertClasses` = root/action/icon/message/filled/
      // outlined/standard/color*). Unconverted v4 Alerts elsewhere still emit
      // the v4 class until their own tranche lands.
      styleOverrides: {
        message: {
          padding: m.alert.messagePadding,
        },
      },
    },
  },
});

export default themeV5;
